/* eslint-disable @next/next/no-img-element */
import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect,
} from "react";
import {
  List,
  ListOrdered,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  X,
  AlertCircle,
  ImageIcon,
} from "lucide-react";
import { DownIcon, UpIcon } from "./icon";

interface ContentBlock {
  type: string;
  value: any;
}

interface Content {
  id: string;
  title: string;
  contentBlocks: ContentBlock[];
}

interface AccordianConfigUiProps {
  contentData?: Content[];
  imageUploadService: (
    formData: FormData
  ) => Promise<{ url: string; alt?: string }>;
}

export interface AccordianConfigUiRef {
  getContentData: () => Content[];
}

const AccordianConfigUi = forwardRef<
  AccordianConfigUiRef,
  AccordianConfigUiProps
>(({ contentData, imageUploadService }, ref) => {
  const [content, setContent] = useState<Content[]>(
    contentData && contentData.length > 0
      ? contentData
      : [
          {
            id: crypto.randomUUID(),
            title: "",
            contentBlocks: [{ type: "text", value: "" }],
          },
        ]
  );
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Expose getContentData method to parent via ref
  useImperativeHandle(ref, () => ({
    getContentData: () => content,
  }));

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const addAccordionItem = () => {
    const newItem: Content = {
      id: crypto.randomUUID(),
      title: "",
      contentBlocks: [{ type: "text", value: "" }],
    };
    setContent([...content, newItem]);
    setExpandedItems((prev) => new Set([...Array.from(prev), newItem.id]));
  };

  const removeAccordionItem = (id: string) => {
    setContent((prev) => prev.filter((item) => item.id !== id));
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  const updateTitle = (id: string, title: string) => {
    setContent((prev) =>
      prev.map((item) => (item.id === id ? { ...item, title } : item))
    );
  };

  const addText = (id: string) => {
    setContent((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              contentBlocks: [
                ...item.contentBlocks,
                { type: "text", value: "" },
              ],
            }
          : item
      )
    );
  };

  const addBulletList = (id: string) => {
    setContent((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              contentBlocks: [
                ...item.contentBlocks,
                { type: "bullet-list", value: [""] },
              ],
            }
          : item
      )
    );
  };

  const addNumberedList = (id: string) => {
    setContent((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              contentBlocks: [
                ...item.contentBlocks,
                { type: "numbered-list", value: [""] },
              ],
            }
          : item
      )
    );
  };

  const addImage = async (id: string) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        const formData = new FormData();
        formData.append("image", file);
        const imageUrl = await imageUploadService(formData);
        setContent((prev) =>
          prev.map((item) =>
            item.id === id
              ? {
                  ...item,
                  contentBlocks: [
                    ...item.contentBlocks,
                    {
                      type: "image",
                      value: {
                        url: imageUrl.url,
                        alt: "",
                      },
                    },
                  ],
                }
              : item
          )
        );
      }
    };
    input.click();
  };

  const updateContentBlock = (
    accordionId: string,
    blockIndex: number,
    updates: Partial<ContentBlock>
  ) => {
    setContent((prev) =>
      prev.map((item) =>
        item.id === accordionId
          ? {
              ...item,
              contentBlocks: item.contentBlocks.map((block, idx) =>
                idx === blockIndex ? { ...block, ...updates } : block
              ),
            }
          : item
      )
    );
  };

  const updateListItem = (
    accordionId: string,
    blockIndex: number,
    itemIndex: number,
    value: string
  ) => {
    setContent((prev) =>
      prev.map((item) =>
        item.id === accordionId
          ? {
              ...item,
              contentBlocks: item.contentBlocks.map((block, idx) => {
                if (idx === blockIndex && Array.isArray(block.value)) {
                  const newValue = [...block.value];
                  newValue[itemIndex] = value;
                  return { ...block, value: newValue };
                }
                return block;
              }),
            }
          : item
      )
    );
  };

  const addListItem = (accordionId: string, blockIndex: number) => {
    setContent((prev) =>
      prev.map((item) =>
        item.id === accordionId
          ? {
              ...item,
              contentBlocks: item.contentBlocks.map((block, idx) => {
                if (idx === blockIndex && Array.isArray(block.value)) {
                  return { ...block, value: [...block.value, ""] };
                }
                return block;
              }),
            }
          : item
      )
    );
  };

  const removeListItem = (
    accordionId: string,
    blockIndex: number,
    itemIndex: number
  ) => {
    setContent((prev) =>
      prev.map((item) =>
        item.id === accordionId
          ? {
              ...item,
              contentBlocks: item.contentBlocks.map((block, idx) => {
                if (
                  idx === blockIndex &&
                  Array.isArray(block.value) &&
                  block.value.length > 1
                ) {
                  const newValue = block.value.filter(
                    (_, i) => i !== itemIndex
                  );
                  return { ...block, value: newValue };
                }
                return block;
              }),
            }
          : item
      )
    );
  };

  const removeBlock = (accordionId: string, blockIndex: number) => {
    setContent((prev) =>
      prev.map((item) =>
        item.id === accordionId
          ? {
              ...item,
              contentBlocks: item.contentBlocks.filter(
                (_, idx) => idx !== blockIndex
              ),
            }
          : item
      )
    );
  };

  const moveBlock = (
    accordionId: string,
    blockIndex: number,
    direction: "up" | "down"
  ) => {
    setContent((prev) =>
      prev.map((item) => {
        if (item.id !== accordionId) return item;

        const blocks = [...item.contentBlocks];
        if (
          (direction === "up" && blockIndex === 0) ||
          (direction === "down" && blockIndex === blocks.length - 1)
        ) {
          return item;
        }

        const newIndex = direction === "up" ? blockIndex - 1 : blockIndex + 1;
        [blocks[blockIndex], blocks[newIndex]] = [
          blocks[newIndex],
          blocks[blockIndex],
        ];

        return { ...item, contentBlocks: blocks };
      })
    );
  };

  const moveAccordionItem = (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === content.length - 1)
    ) {
      return;
    }

    const newContent = [...content];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    [newContent[index], newContent[newIndex]] = [
      newContent[newIndex],
      newContent[index],
    ];
    setContent(newContent);
  };

  return (
    <div className="w-full">
      {/* Accordion Items */}
      <div className="space-y-4">
        {content.map((accordionItem, accordionIndex) => (
          <div
            key={accordionItem.id}
            className="border border-slate-300 rounded-lg overflow-hidden bg-white shadow-sm"
          >
            {/* Accordion Header */}
            <div className="bg-gradient-to-r from-slate-100 to-slate-50 border-b border-slate-300">
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-2 flex-1">
                  <button
                    onClick={() => toggleExpand(accordionItem.id)}
                    className="p-1 hover:bg-slate-200 rounded transition"
                  >
                    {expandedItems.has(accordionItem.id) ? (
                      <ChevronUp size={20} className="text-slate-600" />
                    ) : (
                      <ChevronDown size={20} className="text-slate-600" />
                    )}
                  </button>
                  <input
                    type="text"
                    value={accordionItem.title}
                    onChange={(e) =>
                      updateTitle(accordionItem.id, e.target.value)
                    }
                    className="flex-1 p-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm font-semibold"
                    placeholder="Accordion Title..."
                  />
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <button
                    onClick={() => moveAccordionItem(accordionIndex, "up")}
                    disabled={accordionIndex === 0}
                    className="px-2 py-1 text-xs bg-slate-300 rounded hover:bg-slate-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <UpIcon />
                  </button>
                  <button
                    onClick={() => moveAccordionItem(accordionIndex, "down")}
                    disabled={accordionIndex === content.length - 1}
                    className="px-2 py-1 text-xs bg-slate-300 rounded hover:bg-slate-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <DownIcon />
                  </button>
                  <ConfirmationModal
                    onSuccess={() => removeAccordionItem(accordionItem.id)}
                    title="Confirm Removal"
                    disabled={content.length === 1}
                    caption="Are you sure you want to remove this accordion item? This action cannot be undone."
                  >
                    <button
                      className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                      disabled={content.length === 1}
                    >
                      <Trash2 size={16} />
                    </button>
                  </ConfirmationModal>
                </div>
              </div>
            </div>

            {/* Accordion Content (Collapsible) */}
            {expandedItems.has(accordionItem.id) && (
              <div className="p-4">
                {/* Toolbar */}
                <div className="bg-slate-50 p-3 border border-slate-200 rounded-lg mb-4 flex flex-wrap gap-2 items-center">
                  <button
                    onClick={() => addText(accordionItem.id)}
                    className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded hover:bg-primary-dark transition text-sm"
                  >
                    Text
                  </button>

                  <button
                    onClick={() => addBulletList(accordionItem.id)}
                    className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded hover:bg-primary-dark transition text-sm"
                  >
                    <List size={16} />
                    Bullet List
                  </button>

                  <button
                    onClick={() => addNumberedList(accordionItem.id)}
                    className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded hover:bg-primary-dark transition text-sm"
                  >
                    <ListOrdered size={16} />
                    Numbered List
                  </button>

                  <button
                    onClick={() => addImage(accordionItem.id)}
                    className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded hover:bg-primary-dark transition text-sm"
                  >
                    <ImageIcon size={16} />
                    Image
                  </button>
                </div>

                {/* Content Blocks */}
                <div className="space-y-3">
                  {accordionItem.contentBlocks.map((block, blockIndex) => (
                    <div
                      key={blockIndex}
                      className="p-3 border border-slate-200 rounded bg-slate-50 hover:bg-slate-100 transition"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-semibold text-slate-600 uppercase">
                          {block.type.replace("-", " ")}
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={() =>
                              moveBlock(accordionItem.id, blockIndex, "up")
                            }
                            disabled={blockIndex === 0}
                            className="px-2 py-1 text-xs bg-slate-300 rounded hover:bg-slate-400 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <UpIcon />
                          </button>
                          <button
                            onClick={() =>
                              moveBlock(accordionItem.id, blockIndex, "down")
                            }
                            disabled={
                              blockIndex ===
                              accordionItem.contentBlocks.length - 1
                            }
                            className="px-2 py-1 text-xs bg-slate-300 rounded hover:bg-slate-400 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <DownIcon />
                          </button>
                          <ConfirmationModal
                            onSuccess={() =>
                              removeBlock(accordionItem.id, blockIndex)
                            }
                            title="Confirm Removal"
                            caption="Are you sure you want to remove this block? This action cannot be undone."
                          >
                            <button className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600">
                              <X size={16} />
                            </button>
                          </ConfirmationModal>
                        </div>
                      </div>

                      {block.type === "text" && (
                        <ContentEditableDiv
                          value={block.value}
                          onChange={(newValue) =>
                            updateContentBlock(accordionItem.id, blockIndex, {
                              value: newValue,
                            })
                          }
                          placeholder="Enter your text..."
                        />
                      )}

                      {(block.type === "bullet-list" ||
                        block.type === "numbered-list") && (
                        <div className="space-y-2">
                          {block.value.map(
                            (item: string, itemIndex: number) => (
                              <div key={itemIndex} className="flex gap-2">
                                <span className="text-slate-600 font-bold pt-2">
                                  {block.type === "bullet-list"
                                    ? "•"
                                    : `${itemIndex + 1}.`}
                                </span>
                                <input
                                  type="text"
                                  value={item}
                                  onChange={(e) =>
                                    updateListItem(
                                      accordionItem.id,
                                      blockIndex,
                                      itemIndex,
                                      e.target.value
                                    )
                                  }
                                  className="flex-1 p-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                                  placeholder="List item..."
                                />
                                <button
                                  onClick={() =>
                                    removeListItem(
                                      accordionItem.id,
                                      blockIndex,
                                      itemIndex
                                    )
                                  }
                                  className="px-2 py-1 text-xs bg-transparent  text-red-500 rounded hover:bg-slate-200"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            )
                          )}
                          <button
                            onClick={() =>
                              addListItem(accordionItem.id, blockIndex)
                            }
                            className="w-full py-2 text-sm bg-slate-300 hover:bg-slate-400 rounded transition"
                          >
                            + Add Item
                          </button>
                        </div>
                      )}

                      {block.type === "image" && (
                        <div>
                          <img
                            src={block.value.url}
                            alt={block.value.alt}
                            className="max-w-full h-auto rounded mb-2"
                          />
                          <input
                            type="text"
                            value={block.value.alt}
                            onChange={(e) =>
                              updateContentBlock(accordionItem.id, blockIndex, {
                                value: {
                                  ...block.value,
                                  alt: e.target.value,
                                },
                              })
                            }
                            className="w-full p-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                            placeholder="Image alt text..."
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Accordion Item Button */}
      <div className="mt-4">
        <button
          onClick={addAccordionItem}
          className="flex justify-center cursor-pointer w-full items-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition text-sm font-medium"
        >
          <Plus size={16} />
          Add Accordion Item
        </button>
      </div>
    </div>
  );
});

// ContentEditable component for text blocks
interface ContentEditableDivProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const ContentEditableDiv: React.FC<ContentEditableDivProps> = ({
  value,
  onChange,
  placeholder = "Enter the text here (supports bold, italic, links, etc.)",
}) => {
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (divRef.current && divRef.current.innerHTML !== value) {
      divRef.current.innerHTML = value;
    }
  }, [value]);

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const newValue = e.currentTarget.innerHTML;
    onChange(newValue);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  return (
    <div
      ref={divRef}
      contentEditable
      onInput={handleInput}
      onPaste={handlePaste}
      className="w-full min-h-[80px] p-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
      data-placeholder={placeholder}
      suppressContentEditableWarning
      style={{
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
      }}
    />
  );
};

AccordianConfigUi.displayName = "AccordianConfigUi";

export default AccordianConfigUi;

type ConfirmationModalProps = {
  children: React.ReactNode;
  title: string;
  caption: string;
  onSuccess: () => void;
  onCancel?: () => void;
  disabled?: boolean;
};
const ConfirmationModal = ({
  children,
  title,
  caption,
  onSuccess,
  onCancel,
  disabled = false,
}: ConfirmationModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSuccess = () => {
    onSuccess();
    setIsOpen(false);
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    setIsOpen(false);
  };

  return (
    <>
      <div
        onClick={() => {
          if (!disabled) setIsOpen(true);
        }}
      >
        {children}
      </div>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fade-in">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-red-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {title}
                </h3>
                <p className="text-sm text-gray-600 mb-6">{caption}</p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                  >
                    No, keep it
                  </button>
                  <button
                    onClick={handleSuccess}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                  >
                    Yes, remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
